import { OptionMetadata, SelectedOption, SelectedVariants } from '@/types'
import {
  Button,
  Flex,
  OptionInfoWrapper,
  OptionName,
  OptionPriceWrapper,
  OptionRow,
  Price,
  Quantity,
  Wrapper,
} from './OptionSelector.styles'
import React, { useContext, useEffect, useState } from 'react'
import { createCompositeKey } from '@/helpers/AnalyticsHelper'
import { formatCurrency, ProductOption, ProductSession } from '@big-red-group/storefront-common-checkout'
import { BookingContext } from '@/context/BookingContext'

type OptionSelectorProps = {
  sessions: ProductSession[]
  onQuantityChange: (variant: SelectedVariants, selectedOptions: SelectedOption[]) => void
  getSelectedVariants: () => SelectedVariants
  previouslySelectedValues?: SelectedOption[]
  previouslySelectedSession?: number
  bookingRequired?: boolean
  selectedOptions?: SelectedOption[] | undefined
  showPricing?: boolean
  preselectOption?: boolean
  isExperienceVoucher?: boolean
}

type Option = Record<number, { quantity: number; price: number; name: string; type: string; index: number }>

export const OptionSelector: React.FC<OptionSelectorProps> = ({
  previouslySelectedValues,
  previouslySelectedSession,
  sessions,
  onQuantityChange,
  getSelectedVariants,
  bookingRequired,
  selectedOptions,
  showPricing = false,
  preselectOption = false,
  isExperienceVoucher = false,
}) => {
  const defaultSession = sessions.find((session) => session.id === previouslySelectedSession) || sessions[0]
  const [hasInitialisedOptions, setHasInitialisedOptions] = useState(false)
  const optionsSessionMap: Map<Number, number[]> = new Map()
  const options: Option = {}

  sessions.forEach((session) => {
    session.productOptions.forEach(({ id, price, name, type }, index) => {
      let otherOptions = optionsSessionMap.get(index) || []
      optionsSessionMap.set(index, [...otherOptions, id])
      const previous = previouslySelectedValues?.find((prev) => prev.id === id)
      options[id] = { quantity: previous?.quantity ?? 0, price, name, type, index }
    })
  })

  const [optionQuantities, setOptionQuantities] = useState(options)

  useEffect(() => {
    setOptionQuantities(options)
  }, [previouslySelectedValues])

  useEffect(() => {
    if (preselectOption && !hasInitialisedOptions && defaultSession && !previouslySelectedValues?.length) {
      setHasInitialisedOptions(true)
      handlePlus(
        defaultSession,
        defaultSession.productOptions[0],
        defaultSession.productOptions[0].minQty,
        20,
        defaultSession.productOptions[0].minQty
      )
    }
  }, [])

  const getSelected = (optionQuantity: Option, session: ProductSession, option: ProductOption): SelectedOption[] => {
    return Object.keys(optionQuantity)
      .map((id) => {
        const parsedOptionId = parseInt(id)
        const previous = previouslySelectedValues?.find((prev) => prev.id === parsedOptionId)
        const {
          levy,
          id: productSessionId,
          sessionName: productSessionName,
          tourDuration: productSessionDuration,
          tourHour: productSessionHour,
          tourMinute: productSessionMinute,
        } = session
        const { retailPrice: productOptionRetailPrice } = option
        const optionMetaData: OptionMetadata = {
          ...previous?.metadata,
          levy,
          productOptionRetailPrice,
          productOptionName: optionQuantity[parsedOptionId].name,
          // productSessionName: getSessionNameByOptionId(parsedOptionId),
          productSessionId,
          productSessionName,
          productSessionHour,
          productSessionMinute,
          productSessionDuration,
        }
        let updatedOptionMetaData = optionMetaData
        if (selectedOptions && selectedOptions?.length > 0 && !bookingRequired) {
          selectedOptions.map((selectedOption, index) => {
            if (selectedOption.id === parsedOptionId) {
              updatedOptionMetaData = selectedOptions[index].metadata
            }
          })
        }

        return {
          id: parsedOptionId,
          productMetaData: previous?.productMetaData,
          allIds: optionsSessionMap.get(optionQuantity[parsedOptionId].index) || [],
          price: optionQuantity[parsedOptionId].price,
          quantity: optionQuantity[parsedOptionId].quantity,
          name: optionQuantity[parsedOptionId].name,
          type: optionQuantity[parsedOptionId].type,
          metadata: updatedOptionMetaData,
        }
      })
      .filter((option) => option.quantity > 0)
  }

  const handleMinus = (session: ProductSession, option: ProductOption, minQty: number, multiple: number) => {
    // const cloned = { ...optionQuantities }
    // const newValue = cloned[option.id].quantity === 0 ? 0 : cloned[option.id].quantity - 1
    // cloned[option.id].quantity = newValue
    // setOptionQuantities(cloned)
    // const selected = getSelected(cloned, session)

    // TODO: Can we use the following method? We won't need to use any array methods.
    const newQuantity = (optionQuantities[option.id]?.quantity || 0) - multiple
    const newOptionQuantities: Option = {
      ...optionQuantities,
      [option.id]: {
        ...optionQuantities[option.id],
        quantity: Math.max(newQuantity < minQty ? 0 : newQuantity, 0),
      },
    }
    setOptionQuantities(newOptionQuantities)
    const selected = getSelected(newOptionQuantities, session, option)

    const variantKey = createCompositeKey(session.id, option.id)
    const selectedVariants = getSelectedVariants()

    // Update value of the variant in the map using the composite key
    selectedVariants[variantKey] = {
      ...selectedVariants[variantKey],
      ...session,
      option,
      quantity: newOptionQuantities[option.id].quantity,
    }

    onQuantityChange(selectedVariants, selected)
  }

  const handlePlus = (
    session: ProductSession,
    option: ProductOption,
    minQty: number,
    maxQty: number,
    multiple: number
  ) => {
    // const cloned = { ...optionQuantities }
    // // TODO max 20?
    // const newValue = cloned[option.id].quantity === 20 ? 20 : cloned[option.id].quantity + 1
    // cloned[option.id].quantity = newValue
    // setOptionQuantities(cloned)
    // const selected = getSelected(cloned, session)

    // TODO: Can we use the following method? We won't need to use any array methods.
    const newQuantity = (optionQuantities[option.id]?.quantity || 0) + multiple
    const newOptionQuantities: Option = {
      ...optionQuantities,
      [option.id]: {
        ...optionQuantities[option.id],
        quantity: Math.min(newQuantity < minQty ? minQty : newQuantity, maxQty),
      },
    }
    setOptionQuantities(newOptionQuantities)
    const selected = getSelected(newOptionQuantities, session, option)
    const variantKey = createCompositeKey(session.id, option.id)
    const selectedVariants = getSelectedVariants()

    // Update value of the variant in the map using the composite key
    selectedVariants[variantKey] = {
      ...selectedVariants[variantKey],
      ...session,
      option,
      quantity: newOptionQuantities[option.id].quantity,
    }

    onQuantityChange(selectedVariants, selected)
  }

  const thisSession = sessions.find((session) => session.id === previouslySelectedSession) ?? sessions?.at(0)

  const renderPlusMinusButtons = ({
    session,
    optionIdx,
    multiple,
    maxQty,
    minQty,
    productOptionId,
  }: {
    session: ProductSession
    optionIdx: number
    multiple: number
    maxQty: number
    minQty: number
    productOptionId: number
  }) => {
    return (
      <Flex>
        <Button
          onClick={() => handleMinus(session, session?.productOptions[optionIdx], minQty, multiple)}
          disabled={optionQuantities[productOptionId].quantity === 0}
          data-testid={`selector-minus-${productOptionId}`}
        >
          -
        </Button>
        <Quantity>{optionQuantities[productOptionId].quantity}</Quantity>
        <Button
          onClick={() => handlePlus(session, session?.productOptions[optionIdx], minQty, maxQty, multiple)}
          disabled={optionQuantities[productOptionId].quantity === maxQty}
          data-testid={`selector-plus-${productOptionId}`}
        >
          +
        </Button>
      </Flex>
    )
  }

  const renderOptionInfo = (optionName: string, minQty: number) => {
    return (
      <OptionInfoWrapper>
        <OptionName>
          {/* TODO check longest name - mobile responsiveness */}
          {optionName}
          {/* TODO {description && <span>{description}</span>} */}
          {minQty > 1 && <span>There is a minimum quantity of {minQty} for this option</span>}
        </OptionName>
      </OptionInfoWrapper>
    )
  }

  const renderSessions = () => {
    if (!bookingRequired && sessions.length > 1) {
      return (
        <div>
          {sessions.map((session) => {
            return session?.productOptions?.map(
              ({ name, price, retailPrice, id, maxQty, minQty, multiple }, optionIdx) => (
                <OptionRow key={id} data-testid={`option-row-${id}`}>
                  {renderOptionInfo(`${session.sessionName} ${name}`, minQty)}
                  <OptionPriceWrapper>
                    <Price onSale={retailPrice > price}>
                      <>
                        {retailPrice > price && <s>{formatCurrency(retailPrice)}</s>}
                        {formatCurrency(price)}
                      </>
                    </Price>
                    {renderPlusMinusButtons({ maxQty, minQty, multiple, optionIdx, session, productOptionId: id })}
                  </OptionPriceWrapper>
                </OptionRow>
              )
            )
          })}
        </div>
      )
    }

    return (
      <div key={thisSession?.id}>
        {thisSession?.productOptions?.map(({ name, price, retailPrice, id, maxQty, minQty, multiple }, optionIdx) => (
          <OptionRow key={id} data-testid={`option-row-${id}`}>
            {renderOptionInfo(name, minQty)}
            <OptionPriceWrapper>
              {showPricing && (
                <Price onSale={retailPrice > price}>
                  {bookingRequired ? (
                    <>
                      <span style={{ fontSize: '12px' }}>From</span> {formatCurrency(price)}
                    </>
                  ) : (
                    <>
                      {retailPrice > price && <s>{formatCurrency(retailPrice)}</s>}
                      {formatCurrency(price)}
                    </>
                  )}
                </Price>
              )}
              {renderPlusMinusButtons({
                maxQty,
                minQty,
                multiple,
                optionIdx,
                session: thisSession,
                productOptionId: id,
              })}
            </OptionPriceWrapper>
          </OptionRow>
        ))}
      </div>
    )
  }

  return (
    <>
      <Wrapper>{renderSessions()}</Wrapper>
    </>
  )
}

export default OptionSelector
